'use client';

import { useParams, useSelectedLayoutSegment } from 'next/navigation';
import { useEffect, useState } from 'react';

import { BallCard } from '@/components/molecules/BallCard';
import { BallsSectionSkeleton } from '@/components/organisms/skeletons/BallsSectionSkeleton';
import { useBallSelectionQuery } from '@/hooks/ball-selection/useBallSelectionQuery';
import { useOrganizationStore } from '@/store/organization-store';

export const BallsSection = () => {
	const store = useOrganizationStore();
	const params = useParams<{ teamLeagueId: string }>();
	// The sponsors layout is shared by five spectator tabs, but the ball belongs on Details only.
	// `null` is the index route of that layout; the other tabs report their own segment.
	const isDetailsPage = useSelectedLayoutSegment() === null;
	const isStoreInSyncWithRoute = store.teamLeagueSlug === params.teamLeagueId || store.teamLeagueUuid === params.teamLeagueId;

	const { data: balls, isLoading: isLoadingBalls } = useBallSelectionQuery(isDetailsPage && isStoreInSyncWithRoute);

	const [currentIndex, setCurrentIndex] = useState(0);

	const items = balls?.data;

	useEffect(() => {
		setCurrentIndex(0);
	}, [store.teamLeagueUuid]);

	if (!isDetailsPage || !isStoreInSyncWithRoute) {
		return null;
	}

	if (isLoadingBalls) {
		return <BallsSectionSkeleton />;
	}

	if (!items || items.length === 0) {
		return null;
	}

	// Guards against the index pointing past the end after the list changes. Both handlers step from it
	// rather than from `currentIndex`, so a click is never swallowed by a stale index.
	const safeIndex = Math.min(currentIndex, items.length - 1);

	return (
		<div className="flex w-[320px] flex-col pt-4 md:pt-6">
			<BallCard
				product={items[safeIndex]!}
				ballIndex={safeIndex}
				ballCount={items.length}
				onPreviousBall={() => setCurrentIndex((safeIndex - 1 + items.length) % items.length)}
				onNextBall={() => setCurrentIndex((safeIndex + 1) % items.length)}
			/>
		</div>
	);
};
